import type { FC, ReactNode } from 'react'

import { Download, FlipHorizontal2, Gauge, ZoomIn } from 'lucide-react'
import MediaTile from '@/components/landing/mockups/media-tile'
import { MangaScreen } from '@/components/landing/mockups/layout-screen'
import { ScreenChip, Scrubber } from '@/components/landing/mockups/screen-ui'
import { TEAL_TONE, photos } from '@/constants/media'

// Two moments of the same jump: the full take and a zoomed, slowed-down detail.
export const SportsScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 flex gap-[0.5cqw]'>
            <div className='relative h-full flex-1 overflow-hidden'>
                <MediaTile src={photos.parkour} position='55% 42%' />

                <ScreenChip className='left-[5cqw] top-[2.6cqw]' icon={<Gauge className='size-[1.6cqw]' />}>
                    Take 1 · 0.5x
                </ScreenChip>

                <Scrubber progress={38} className='left-[5cqw]' />
            </div>

            <div className='relative h-full flex-1 overflow-hidden'>
                <MediaTile src={photos.parkour} position='62% 32%' zoom={1.8} />

                <ScreenChip className='left-[2.4cqw] top-[2.6cqw]' tone='cyan' icon={<ZoomIn className='size-[1.6cqw]' />}>
                    Take 2 · 1.8x zoom
                </ScreenChip>

                <Scrubber progress={64} color='bg-cyan-300' />
            </div>
        </div>
    )
}

// A clip next to its own mirrored copy.
export const MirrorScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 flex gap-[0.5cqw]'>
            <div className='relative h-full flex-1 overflow-hidden'>
                <MediaTile src={photos.breakdance} position='50% 40%' />

                <ScreenChip className='left-[5cqw] top-[2.6cqw]'>
                    Reference
                </ScreenChip>

                <ScreenChip className='bottom-[2.4cqw] left-[5cqw]' icon={<Gauge className='size-[1.6cqw]' />}>
                    0.75x
                </ScreenChip>
            </div>

            <div className='relative h-full flex-1 overflow-hidden'>
                <MediaTile src={photos.breakdance} position='50% 40%' mirror />

                <ScreenChip className='right-[2.6cqw] top-[2.6cqw]' tone='violet' icon={<FlipHorizontal2 className='size-[1.6cqw]' />}>
                    Mirror
                </ScreenChip>

                <ScreenChip className='bottom-[2.4cqw] right-[2.6cqw]' icon={<Gauge className='size-[1.6cqw]' />}>
                    0.75x
                </ScreenChip>
            </div>

            <span className='absolute left-1/2 top-1/2 z-10 grid size-[5.4cqw] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white text-slate-900 shadow-[0_0.6cqw_2cqw_rgb(0_0_0/0.35)]'>
                <FlipHorizontal2 className='size-[2.6cqw]' />
            </span>
        </div>
    )
}

// Four clips arranged in Manga panels, ready to be exported as one scene.
export const CreationScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0'>
            <MangaScreen
                looks={[
                    { src: photos.dancer, position: '45% 55%' },
                    { src: photos.neonCity, position: '70% 30%' },
                    { src: photos.surf, position: '60% 50%', filter: TEAL_TONE },
                    { src: photos.waterfall, position: '40% 50%' }
                ]}
            />

            <ScreenChip className='right-[3cqw] top-[2.6cqw] z-20' tone='light' icon={<Download className='size-[1.6cqw]' />}>
                MP4 · 720p
            </ScreenChip>
        </div>
    )
}
